import { useCallback, useMemo } from 'react';
import filter from 'lodash/filter';
import first from 'lodash/first';
import concat from 'lodash/concat';
import isEmpty from 'lodash/isEmpty';
import trim from 'lodash/trim';
import compact from 'lodash/compact';
import reduce from 'lodash/reduce';
import find from 'lodash/find';
import uniq from 'lodash/uniq';
import map from 'lodash/map';
import omit from 'lodash/omit';

import {
  ChangeLifestylesFiltersFunc,
  FetchLifestylesFilterFields,
  FetchLifestylesFilters
} from '../../../../../../../../lifestyles/lifestylesTypes';
import { IdFilterType } from '../../../../../../../../../types';
import {
  CategoryID,
  CategoryLocalizedName
} from '../../../../../../../../categories/categoriesTypes';
import { FetchLifestyleCategoriesQueryResponse } from '../../../../../../../../lifestyles/queries/fetchLifestyleCategories.query';

import { useThreeDStockCategories } from '../../../../../../../hooks/useThreeDStockCategories';
import { useCacheLifestyleCategories } from '../../../../../../../../lifestyles/hooks/useCacheLifestyleCategories';

import { getChangeLifestylesFiltersCategoriesData } from '../../../../../../../../lifestyles/utils/getChangeLifestylesFiltersCategoriesData';

import { getAllParentsCategories } from '../../../../../../../../categories/utils/getAllParentsCategories';

import { StockTabs } from '../../../../../../../stockConstants';
import { LifestyleCache } from '../../../../../../../../lifestyles/LifestyleCache';

interface LifestylesFilterCategoriesOptions {
  lifestylesFilters: FetchLifestylesFilters;
  defaultLifestyleCategoryId?: IdFilterType;
  changeLifestylesFilters: ChangeLifestylesFiltersFunc;
  checkedCategories: { id: CategoryID; localizedName: CategoryLocalizedName }[];
  setCheckedCategories: (
    checkedCategories: {
      id: CategoryID;
      localizedName: CategoryLocalizedName;
    }[]
  ) => void;
  changeCategory: ChangeLifestylesFiltersFunc;
}

function useLifestylesFilterCategories({
  lifestylesFilters,
  defaultLifestyleCategoryId,
  changeLifestylesFilters,
  checkedCategories,
  setCheckedCategories,
  changeCategory
}: LifestylesFilterCategoriesOptions) {
  const checkedCategoryIds = map(checkedCategories, 'id');

  const {
    categories: lifestyleCategoriesWithParent,
    categoriesFetched: allLifestyleCategoriesWithParentFetched,
    currentCategory
  } = useThreeDStockCategories({
    tab: StockTabs.LIFESTYLES,
    categoryIds: defaultLifestyleCategoryId?.in
  });

  const { lifestyleCategories, lifestyleCategoriesFetched } =
    useCacheLifestyleCategories({
      cacheKey: LifestyleCache.notEmptyCategoriesCacheKey(),
      roomsByLifestyle: omit(lifestylesFilters, [
        'lifestyleCategoryId',
        'lifestyleParentCategoryIds'
      ])
    });

  const listCategories = useMemo<
    FetchLifestyleCategoriesQueryResponse[]
  >(() => {
    const defaultCurrentCategoryId = first(defaultLifestyleCategoryId?.in);

    return getAllParentsCategories(
      lifestyleCategoriesWithParent,
      lifestyleCategories,
      defaultCurrentCategoryId
    )?.sort((a, b) => {
      return (
        trim(a.localizedName)?.localeCompare(trim(b.localizedName), undefined, {
          numeric: true,
          sensitivity: 'base'
        }) || 0
      );
    });
  }, [
    defaultLifestyleCategoryId?.in,
    lifestyleCategories,
    lifestyleCategoriesWithParent
  ]);

  const handleCheckedCategory = useCallback<(categoryId: CategoryID) => void>(
    (categoryId) => {
      const isChecked = checkedCategoryIds?.includes(categoryId);

      const newCheckedCategoryIds = isChecked
        ? checkedCategoryIds.filter((id) => id !== categoryId)
        : [...checkedCategoryIds, categoryId];

      const newCheckedCategories = filter(
        lifestyleCategoriesWithParent,
        (category) => newCheckedCategoryIds.includes(category.id)
      );

      setCheckedCategories(newCheckedCategories);

      if (isEmpty(newCheckedCategoryIds)) {
        const { changeFilters, resetFilters } =
          getChangeLifestylesFiltersCategoriesData({
            lifestyleCategory: currentCategory,
            categories: lifestyleCategoriesWithParent
          });

        return changeLifestylesFilters(changeFilters, resetFilters);
      }

      const combinedFilters = reduce(
        newCheckedCategoryIds,
        (result, categoryId) => {
          const category = find(lifestyleCategoriesWithParent, [
            'id',
            categoryId
          ]);

          const { changeFilters } = getChangeLifestylesFiltersCategoriesData({
            lifestyleCategory: category,
            categories: lifestyleCategoriesWithParent
          });

          return {
            [FetchLifestylesFilterFields.LIFESTYLE_PARENT_CATEGORY_IDS]: uniq(
              compact(
                concat(
                  result[
                    FetchLifestylesFilterFields.LIFESTYLE_PARENT_CATEGORY_IDS
                  ],
                  changeFilters[
                    FetchLifestylesFilterFields.LIFESTYLE_PARENT_CATEGORY_IDS
                  ]
                )
              )
            ),
            [FetchLifestylesFilterFields.LIFESTYLE_CATEGORY_ID]: {
              in: uniq(
                compact(
                  concat(
                    result[FetchLifestylesFilterFields.LIFESTYLE_CATEGORY_ID]
                      ?.in,
                    changeFilters[
                      FetchLifestylesFilterFields.LIFESTYLE_CATEGORY_ID
                    ]?.in
                  )
                )
              )
            }
          };
        },
        {
          [FetchLifestylesFilterFields.LIFESTYLE_PARENT_CATEGORY_IDS]: [],
          [FetchLifestylesFilterFields.LIFESTYLE_CATEGORY_ID]: { in: [] }
        }
      );

      const resetFilters = compact([
        !isEmpty(
          combinedFilters[FetchLifestylesFilterFields.LIFESTYLE_CATEGORY_ID]?.in
        )
          ? null
          : FetchLifestylesFilterFields.LIFESTYLE_CATEGORY_ID,
        !isEmpty(
          combinedFilters[
            FetchLifestylesFilterFields.LIFESTYLE_PARENT_CATEGORY_IDS
          ]
        )
          ? null
          : FetchLifestylesFilterFields.LIFESTYLE_PARENT_CATEGORY_IDS
      ]);

      changeLifestylesFilters(combinedFilters, resetFilters);
    },
    [
      changeLifestylesFilters,
      checkedCategoryIds,
      currentCategory,
      lifestyleCategoriesWithParent,
      setCheckedCategories
    ]
  );

  const handleChangeCategory = useCallback<(categoryId: CategoryID) => void>(
    (categoryId) => {
      const lifestyleCategory = find(lifestyleCategoriesWithParent, [
        'id',
        categoryId
      ]);

      const { changeFilters, resetFilters } =
        getChangeLifestylesFiltersCategoriesData({
          lifestyleCategory,
          categories: lifestyleCategoriesWithParent
        });

      changeCategory(changeFilters, resetFilters);
    },
    [changeCategory, lifestyleCategoriesWithParent]
  );

  return {
    listCategories,
    listCategoriesFetched:
      allLifestyleCategoriesWithParentFetched && lifestyleCategoriesFetched,
    checkedCategoryIds,
    handleCheckedCategory,
    handleChangeCategory
  };
}

export default useLifestylesFilterCategories;
